import { useTheme } from '@/components/ThemeProvider';
import { cn, genJudgingSizeColor } from '@/lib/utils';
import { SubscribeEvent } from '@/interface/event';
import { ArrowDown } from '@/widget/Icon';
import { useAppEventBusContext } from '@/context/eventBusContext';
import { formatCryptoPrice, formatPercentage, formatTextEllipsis, formatValueWithCompactNumber } from '@/lib/format';
import { useTranslation } from 'react-i18next';
import { Skeleton } from '@/components/ui/skeleton';
import TokenAvatar from '@/widget/TokenAvatar';
import BigNumber from 'bignumber.js';
import { useTradeStore } from '../../store';
import { useAppStore } from '@/store';
import CopyNode from '@/widget/CopyNode';
import StarNode from '@/widget/StarNode';
import { useBoomWallet } from '@boomeme/wallet-sdk';

interface IProps {
  showTop10?: boolean;
}
export default (props: IProps) => {
  const { isConnected } = useBoomWallet();
  const { showTop10 = false } = props;
  const { fwClass } = useAppStore();
  const { tokenInfo, tokenRealtimePrice, priceChg } = useTradeStore();
  const { theme } = useTheme();
  const { t } = useTranslation();
  const { publish } = useAppEventBusContext();

  const marketCap = tokenInfo?.address ? BigNumber(tokenInfo.totalSupply).times(tokenInfo.price).toString() : 0;

  if (!tokenInfo?.address) {
    return <Skeleton className="w-full h-[88px]"></Skeleton>;
  }

  return (
    <div className="flex justify-between">
      <div>
        <div className="mb-[10px] flex items-center gap-x-[12px]">
          <TokenAvatar address={tokenInfo.address} logo={tokenInfo.logo} className="w-[30px] h-[30px]" />
          <div>
            <div className="flex items-center" onClick={() => publish(SubscribeEvent.SHOW_SEARCH_SHEET, true)}>
              <span className={cn('text-[24px] max-w-[100px] overflow-hidden text-ellipsis', fwClass)}>{tokenInfo.symbol}</span>
              <ArrowDown theme={theme} className="w-[24px] h-[24px]" />
            </div>
            <p className="text-[12px] text-secondaryTextColor flex items-center gap-x-[16px]">
              {formatTextEllipsis(tokenInfo.address)}
              <CopyNode text={tokenInfo.address} className="size-[14px] opacity-[0.4]" />
              {isConnected && <StarNode tokenAddress={tokenInfo.address} className="size-[18px]" />}
            </p>
          </div>
        </div>
        <div className="flex items-center">
          <span className={cn(genJudgingSizeColor(priceChg), fwClass)}>
            ${formatCryptoPrice(tokenRealtimePrice)}&nbsp;{`(${formatPercentage(priceChg, { scale: true })})`}
          </span>
        </div>
      </div>
      <div className="text-[12px] font-[400] flex flex-col justify-between min-w-[110px] w-[130px]">
        <div className="flex justify-between">
          <span className="text-secondaryTextColor">{t('Trade.mcap')}</span>
          <span>{formatValueWithCompactNumber(marketCap, true)}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-secondaryTextColor">{t('Trade.liq')}</span>
          <span>{formatValueWithCompactNumber(tokenInfo?.liquidity, true)}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-secondaryTextColor">{t('Trade.24hVol')}</span>
          <span>{formatValueWithCompactNumber(tokenInfo.volume, true)}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-secondaryTextColor">{t('Trade.holders')}</span>
          <span>{Number(tokenInfo.holders).toLocaleString()}</span>
        </div>
        {showTop10 && (
          <div className="flex justify-between">
            <span className="text-secondaryTextColor">Top 10</span>
            <span>{formatPercentage(tokenInfo.top10Percent, { scale: true, showPositiveSign: false })}</span>
          </div>
        )}
      </div>
    </div>
  );
};
